import type { PoiData } from '$lib/components/pois/types';
import type { WayPoint } from '$lib/components/search/itinerary/types';
import type { PoisStoreContent } from '$routes/(map)/types';
import type { SelectedAddress } from '../urls/searchParams';
import type { SearchExtensionContextContent, SearchExtensionContextCoreContent } from './types';
import { get, readonly, writable } from 'svelte/store';

const initialValue: SearchExtensionContextContent = {
	address: undefined,
	coordinates: undefined,
	updateCount: 0,
};

const store = writable(initialValue);

function isEqual(
	currentValue: SearchExtensionContextCoreContent,
	newValue: SearchExtensionContextCoreContent,
) {
	return (
		currentValue.coordinates?.lat === newValue.coordinates?.lat
		&& currentValue.coordinates?.lng === newValue.coordinates?.lng
		&& currentValue.address === newValue.address
	);
}

function isDefined(newValue: SearchExtensionContextCoreContent) {
	return newValue.address && newValue.coordinates;
}

function hasContext() {
	const searchExtensionContext = get(store);
	return !isEqual(searchExtensionContext, initialValue);
}

const { update: storeUpdate } = store;

async function updateContext(newValue: SearchExtensionContextCoreContent) {
	if (!isDefined(newValue)) {
		return;
	}

	const { updateCount, ...currentCoreValue } = get(store);

	if (!isEqual(currentCoreValue, newValue)) {
		storeUpdate(() => ({ ...newValue, updateCount: updateCount + 1 }));
	}
}

async function updateWithSelectedPoiData(selectedPoiData: PoiData) {
	const newValue = {
		address: selectedPoiData.address.city ?? undefined,
		coordinates: selectedPoiData.coordinates,
	};
	updateContext(newValue);
}

async function updateWithSelectedAddress(selectedAddress: SelectedAddress) {
	const newValue = {
		address: selectedAddress?.address,
		coordinates: selectedAddress?.coordinates,
	};
	updateContext(newValue);
}

async function updateFromPoisStore(poisStore: PoisStoreContent | undefined) {
	if (!poisStore) {
		return;
	}

	const { selectedPoiData, selectedAddress } = poisStore;

	if (selectedPoiData) {
		updateWithSelectedPoiData(selectedPoiData);
	} else if (selectedAddress) {
		updateWithSelectedAddress(selectedAddress);
	}
}

async function updateWithWaypoints(waypoints: WayPoint[]) {
	const arrival = waypoints?.find(waypoint => waypoint.isArrival === true);
	const newValue = {
		address: arrival?.label,
		coordinates: arrival?.coordinates === 'MY_LOC' ? undefined : arrival?.coordinates,
	};
	updateContext(newValue);
}

async function updateFromItineraryStore(itineraryStoreContent: WayPoint[]) {
	updateWithWaypoints(itineraryStoreContent);
}

export const searchExtensionContext = {
	...readonly(store),
	hasContext,
	updateFromPoisStore,
	updateFromItineraryStore,
};
